import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { useAccessApi, Organization, SsoConnectionStatus } from '../services/api';
import { useAdminCompany } from '../hooks/useApi';
import { auth0Config } from '../config/auth0';
import { cn } from '../lib/utils';
import { LoadingState } from './LoadingSpinner';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Field } from './ui/form-field';
import { Input } from './ui/input';
import { ConfirmDialog } from './ui/confirm-dialog';
import { DialogFooter, DialogFrame } from './ui/dialog-frame';
import { Spinner } from './ui/spinner';
import { InlineAlert } from './ui/inline-alert';
import { textLinkClassName } from './ui/link-styles';
import { fieldLabelClassName, overlineClassName, pageTitleClassName } from './ui/typography';
import {
    ArrowLeft,
    Check,
    ClipboardList,
    Copy,
    ExternalLink,
    CircleCheck,
    Clock,
    Globe,
    KeyRound,
    Shield,
    ShieldCheck,
    Ticket,
    Trash2,
} from 'lucide-react';
import { announce } from './ui/live-announcer';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import {
    ADMIN_BADGE_BASE_CLASS,
    ADMIN_BADGE_POSITIVE_CLASS,
    ADMIN_BADGE_WARNING_CLASS,
} from './admin/credit/adminStatusBadge';

interface SelfServiceProfile {
    id: string;
    name: string;
    description?: string;
    branding?: unknown;
    user_attributes?: unknown;
    allowed_strategies?: string[];
}

interface ExistingTicket {
    ticketUrl: string;
    ticketId: string;
    issuedAt: string;
    expiresAt: string;
    organizationDisplayName: string;
}

const SSO_STRATEGY_LABELS: Record<string, string> = {
    okta: 'Okta',
    waad: 'Microsoft Entra ID',
    samlp: 'SAML',
    oidc: 'OpenID Connect',
    adfs: 'ADFS',
    'google-apps': 'Google Workspace',
    pingfederate: 'PingFederate',
    ad: 'Active Directory',
};

function formatSsoStrategyLabel(strategy: string): string {
    return SSO_STRATEGY_LABELS[strategy] ?? strategy;
}

export function SsoAdmin() {
    useDocumentTitle('Manage SSO');
    const { orgId: routeOrgId, companyId } = useParams<{ orgId?: string; companyId?: string }>();
    const numericCompanyId = companyId ? Number(companyId) : null;
    const companyQuery = useAdminCompany(
        numericCompanyId != null && Number.isFinite(numericCompanyId) && numericCompanyId > 0
            ? numericCompanyId
            : null,
    );
    const orgId = routeOrgId ?? companyQuery.data?.organizationId ?? undefined;
    const isCompanyScoped = Boolean(companyId);
    const { hasPermission } = useUserPermissions();
    const {
        getOrganizations,
        getSelfServiceProfiles,
        getSSOTicket,
        revokeSSOTicket,
        generateSSOTicket,
        getSSOConnectionStatus,
    } = useAccessApi();
    const navigate = useNavigate();
    const [organization, setOrganization] = useState<Organization | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Multi-step wizard state (in modal)
    const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);
    const [profiles, setProfiles] = useState<SelfServiceProfile[]>([]);
    const [selectedProfile, setSelectedProfile] = useState<SelfServiceProfile | null>(null);
    const [isLoadingProfiles, setIsLoadingProfiles] = useState(false);
    const [profilesError, setProfilesError] = useState<string | null>(null);

    // Ticket generation state
    const [ticketUrl, setTicketUrl] = useState<string>('');
    const [isGeneratingTicket, setIsGeneratingTicket] = useState(false);
    const [ticketError, setTicketError] = useState<string | null>(null);
    const [copySuccess, setCopySuccess] = useState(false);
    const copyButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (ticketUrl) copyButtonRef.current?.focus();
    }, [ticketUrl]);

    // Domain aliases state
    const [domainAliases, setDomainAliases] = useState<string>('');
    const [domainAliasError, setDomainAliasError] = useState<string | null>(null);
    const domainAliasInputRef = useRef<HTMLInputElement>(null);

    // Existing ticket state
    const [existingTicket, setExistingTicket] = useState<ExistingTicket | null>(null);
    const [isLoadingExistingTicket, setIsLoadingExistingTicket] = useState(true);
    const [isRevokingTicket, setIsRevokingTicket] = useState(false);
    const [isRevokeConfirmOpen, setIsRevokeConfirmOpen] = useState(false);
    const [revokeError, setRevokeError] = useState<string | null>(null);
    const [existingTicketCopySuccess, setExistingTicketCopySuccess] = useState(false);
    const pageHeadingRef = useRef<HTMLHeadingElement>(null);

    // Confirmation modal state
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);

    // SSO Connection status state
    const [ssoConnectionStatus, setSsoConnectionStatus] = useState<SsoConnectionStatus | null>(
        null,
    );
    const [isLoadingConnectionStatus, setIsLoadingConnectionStatus] = useState(true);
    const [connectionStatusError, setConnectionStatusError] = useState<string | null>(null);

    // Construct Auth0 management URL
    const match = auth0Config.domain.match(/^auth-(.*?)\.wevo\.ai$/);
    const auth0OrgUrl = match ? `https://manage.auth0.com/dashboard/us/wevo-${match[1]}` : null;

    // Fetch organization details
    useEffect(() => {
        const fetchOrganization = async () => {
            if (!orgId) return;

            try {
                setIsLoading(true);
                setError(null);

                // Get organization details
                const orgs = await getOrganizations();
                const org = orgs.find((o) => o.id === orgId);
                if (!org) {
                    setError('Organization not found.');
                    return;
                }
                setOrganization(org);
            } catch (err) {
                console.error('Error fetching organization data:', err);
                setError("Couldn't load this organization. Refresh the page to try again.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchOrganization();
    }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch existing SSO ticket
    useEffect(() => {
        const fetchExistingTicket = async () => {
            if (!orgId) return;

            try {
                setIsLoadingExistingTicket(true);
                const response = await getSSOTicket(orgId);

                if (response.success && response.hasTicket && response.ticket) {
                    setExistingTicket(response.ticket);
                } else {
                    setExistingTicket(null);
                }
            } catch (err) {
                console.error('Error fetching existing SSO ticket:', err);
                setExistingTicket(null);
            } finally {
                setIsLoadingExistingTicket(false);
            }
        };

        fetchExistingTicket();
    }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Function to fetch SSO connection status (extracted for reuse)
    const fetchConnectionStatus = async () => {
        if (!orgId) return;

        try {
            setIsLoadingConnectionStatus(true);
            setConnectionStatusError(null);

            const response = await getSSOConnectionStatus(orgId);
            if (response.success) {
                setSsoConnectionStatus(response.data);
            } else {
                setConnectionStatusError(
                    "Couldn't check the SSO connection status. Refresh the page to try again.",
                );
            }
        } catch (err) {
            console.error('Error fetching SSO connection status:', err);
            setConnectionStatusError(
                "Couldn't check the SSO connection status. Refresh the page to try again.",
            );
        } finally {
            setIsLoadingConnectionStatus(false);
        }
    };

    // Fetch SSO connection status on component mount/orgId change
    useEffect(() => {
        fetchConnectionStatus();
    }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-revoke existing ticket when SSO connection is active
    useEffect(() => {
        const autoRevokeTicketIfConnectionActive = async () => {
            // Only proceed if we have both states loaded and SSO connection exists
            if (
                !isLoadingConnectionStatus &&
                !isLoadingExistingTicket &&
                ssoConnectionStatus?.exists &&
                existingTicket &&
                orgId
            ) {
                try {
                    console.log('SSO connection is active - auto-revoking existing ticket');
                    await revokeSSOTicket(orgId);

                    // Clear the existing ticket from state since it's been revoked
                    setExistingTicket(null);

                    console.log('Successfully auto-revoked SSO ticket');
                } catch (err) {
                    console.error('Error auto-revoking SSO ticket:', err);
                    // Don't show error to user since this is automatic cleanup
                }
            }
        };

        autoRevokeTicketIfConnectionActive();
    }, [
        isLoadingConnectionStatus,
        isLoadingExistingTicket,
        ssoConnectionStatus,
        existingTicket,
        orgId,
        revokeSSOTicket,
    ]);

    // Only show this component if user has administrate:organizations permission
    if (!hasPermission('administrate:organizations')) {
        return null;
    }

    const handleBackToAdmin = () => {
        if (isCompanyScoped && companyId) {
            navigate(`/admin/companies/${companyId}`);
            return;
        }
        navigate(`/admin/${orgId}`);
    };

    const handleOpenModal = () => {
        setIsModalOpen(true);
        handleStartWizard();
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        // Reset wizard state
        setCurrentStep(1);
        setProfiles([]);
        setSelectedProfile(null);
        setProfilesError(null);
        setTicketUrl('');
        setTicketError(null);
        setCopySuccess(false);
        setDomainAliases('');
        setDomainAliasError(null);
    };

    // Handle successful completion of SSO setup (refresh states)
    const handleSuccessfulCompletion = async () => {
        // Close modal and reset wizard state
        handleCloseModal();

        // Refresh connection status to show new SSO connection
        await fetchConnectionStatus();

        // Also refresh existing ticket state in case it was auto-revoked
        if (orgId) {
            try {
                setIsLoadingExistingTicket(true);
                const response = await getSSOTicket(orgId);

                if (response.success && response.hasTicket && response.ticket) {
                    setExistingTicket(response.ticket);
                } else {
                    setExistingTicket(null);
                }
            } catch (err) {
                console.error('Error fetching existing SSO ticket after completion:', err);
                setExistingTicket(null);
            } finally {
                setIsLoadingExistingTicket(false);
            }
        }
    };

    const handleStartWizard = async () => {
        setIsLoadingProfiles(true);
        setProfilesError(null);
        setCurrentStep(1);

        try {
            const response = await getSelfServiceProfiles();
            setProfiles(response.profiles);
        } catch (err) {
            console.error('Failed to fetch Self-Service Profiles:', err);
            setProfilesError(
                err instanceof Error
                    ? err.message
                    : 'Could not load Self-Service Profiles. Close the wizard and try again.',
            );
        } finally {
            setIsLoadingProfiles(false);
        }
    };

    const handleProfileSelect = (profile: SelfServiceProfile) => {
        setSelectedProfile(profile);
        setCurrentStep(2);
    };

    const handleBackToProfileSelection = () => {
        setCurrentStep(1);
        setTicketUrl('');
        setTicketError(null);
    };

    const handleContinueToGenerate = () => {
        // Validate domain aliases before proceeding
        const validation = validateDomainAliases(domainAliases);
        if (!validation.isValid) {
            setDomainAliasError(
                validation.error || 'Enter domain names like company.com, separated by commas.',
            );
            domainAliasInputRef.current?.focus();
            return;
        }

        setCurrentStep(3);
        setTicketError(null);
        setDomainAliasError(null);
    };

    const handleBackToDomainAliases = () => {
        setCurrentStep(2);
        setTicketUrl('');
        setTicketError(null);
    };

    // Validate and clean domain aliases
    const validateDomainAliases = (
        input: string,
    ): { isValid: boolean; cleanedDomains: string[]; error?: string } => {
        if (!input.trim()) {
            return { isValid: true, cleanedDomains: [] }; // Empty is valid (optional)
        }

        const domains = input
            .split(',')
            .map((domain) => domain.trim())
            .map((domain) => domain.replace(/^@/, '')) // Remove leading @ if present
            .filter((domain) => domain.length > 0);

        // Basic domain validation regex
        const domainRegex =
            /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

        const invalidDomains = domains.filter((domain) => !domainRegex.test(domain));

        if (invalidDomains.length > 0) {
            return {
                isValid: false,
                cleanedDomains: [],
                error: `Invalid domain format: ${invalidDomains.join(', ')}. Use domain names like 'company.com' (no @ symbols)`,
            };
        }

        return { isValid: true, cleanedDomains: domains };
    };

    const handleDomainAliasChange = (value: string) => {
        setDomainAliases(value);

        // Clear error when user starts typing
        if (domainAliasError) {
            setDomainAliasError(null);
        }
    };

    const handleDomainAliasBlur = () => {
        const validation = validateDomainAliases(domainAliases);
        if (domainAliases.trim() && !validation.isValid) {
            setDomainAliasError(
                validation.error || 'Enter domain names like company.com, separated by commas.',
            );
        }
    };

    const handleGenerateTicket = async () => {
        if (!orgId || !selectedProfile) return;

        setIsGeneratingTicket(true);
        setTicketError(null);

        try {
            // Validate and clean domain aliases
            const validation = validateDomainAliases(domainAliases);
            if (!validation.isValid) {
                setTicketError(
                    validation.error || 'Enter domain names like company.com, separated by commas.',
                );
                return;
            }

            const response = await generateSSOTicket(
                orgId,
                selectedProfile.id,
                validation.cleanedDomains,
            );
            setTicketUrl(response.ticketUrl);
        } catch (err) {
            console.error('Failed to generate SSO ticket:', err);

            // Try to parse the enhanced error response from the backend
            let errorMessage = 'Could not generate the SSO ticket. Try again.';
            let errorDetails: string[] = [];

            if (err instanceof Error) {
                // Check if this is a structured error response from our backend
                try {
                    // The API service throws the error.message from the response
                    // For our enhanced errors, this will be the user-friendly message
                    errorMessage = err.message;

                    // For connection already exists error, provide helpful guidance
                    if (errorMessage.includes('already exists')) {
                        errorDetails = [
                            'This organization may already have SSO configured.',
                            'Check your existing connections in the Auth0 dashboard.',
                            'Contact support if you need to update an existing connection.',
                        ];
                    } else if (errorMessage.includes('invalid data')) {
                        errorDetails = [
                            'Please check your domain aliases format.',
                            'Domain aliases should be in the format: company.com',
                            'Multiple domains should be separated by commas.',
                        ];
                    }
                } catch (parseError) {
                    console.error('Error parsing backend response:', parseError);
                }
            }

            // Create a more detailed error message
            let fullErrorMessage = errorMessage;
            if (errorDetails.length > 0) {
                fullErrorMessage += '\n\nSuggested actions:\n• ' + errorDetails.join('\n• ');
            }

            setTicketError(fullErrorMessage);
        } finally {
            setIsGeneratingTicket(false);
        }
    };

    const handleCopyUrl = async () => {
        if (!ticketUrl) return;

        try {
            await navigator.clipboard.writeText(ticketUrl);
            setCopySuccess(true);
            announce('Copied');
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (err) {
            console.error('Failed to copy URL:', err);
        }
    };

    const handleCopyExistingUrl = async () => {
        if (!existingTicket) return;

        try {
            await navigator.clipboard.writeText(existingTicket.ticketUrl);
            setExistingTicketCopySuccess(true);
            announce('Copied');
            setTimeout(() => setExistingTicketCopySuccess(false), 2000);
        } catch (err) {
            console.error('Failed to copy existing ticket URL:', err);
        }
    };

    const handleRevokeTicket = async () => {
        if (!orgId || !existingTicket) return;

        try {
            setIsRevokingTicket(true);
            await revokeSSOTicket(orgId);

            // Clear the existing ticket from state
            setExistingTicket(null);
            announce('SSO ticket revoked');
            window.requestAnimationFrame(() => pageHeadingRef.current?.focus());
        } catch (err) {
            console.error('Failed to revoke SSO ticket:', err);
            setRevokeError("Couldn't revoke the SSO ticket. Try again.");
        } finally {
            setIsRevokingTicket(false);
        }
    };

    const handleSetupSSOClick = () => {
        if (existingTicket) {
            // Show confirmation modal if there's an existing ticket
            setShowConfirmationModal(true);
        } else {
            // No existing ticket, proceed directly
            handleOpenModal();
        }
    };

    const handleConfirmNewTicket = () => {
        setShowConfirmationModal(false);
        handleOpenModal();
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString(undefined, {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    };

    if (isCompanyScoped && companyQuery.isSuccess && !companyQuery.data?.organizationId) {
        return (
            <div className="space-y-6">
                <Card>
                    <CardHeader className="bg-muted/40">
                        <CardTitle as="h1" className={overlineClassName}>
                            SSO management
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                        <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
                            <p className="text-amber-800">
                                Sync this company to Auth0 before managing SSO.
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if ((isCompanyScoped && companyQuery.isLoading) || isLoading || isLoadingConnectionStatus) {
        return (
            <div className="space-y-6">
                <Card>
                    <CardHeader className="bg-muted/40">
                        <CardTitle as="h1" className={overlineClassName}>
                            SSO management
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                        <LoadingState />
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (error || companyQuery.isError) {
        return (
            <div className="space-y-6">
                <Card>
                    <CardHeader className="bg-muted/40">
                        <CardTitle as="h1" className={overlineClassName}>
                            SSO management
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                        <InlineAlert tone="error">
                            {error ??
                                "Couldn't load this company's SSO settings. Refresh the page to try again."}
                        </InlineAlert>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const isTicketExpired = existingTicket
        ? new Date(existingTicket.expiresAt).getTime() <= Date.now()
        : false;

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="flex items-start gap-3">
                    <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Back"
                        onClick={handleBackToAdmin}
                    >
                        <ArrowLeft className="size-5" aria-hidden />
                    </Button>
                    <div className="min-w-0">
                        <p className="text-sm text-neutral-500">Company SSO</p>
                        <h1
                            ref={pageHeadingRef}
                            tabIndex={-1}
                            className={cn(pageTitleClassName, 'focus:outline-none')}
                        >
                            Manage SSO
                        </h1>
                        <p className="mt-1 text-sm text-neutral-500">
                            {companyQuery.data?.name ||
                                organization?.display_name ||
                                organization?.name}
                        </p>
                    </div>
                </div>
            </div>

            {/* Main SSO Configuration Card */}

            <Card>
                <CardHeader className="bg-muted/40">
                    <CardTitle as="h2" className={overlineClassName}>
                        Self-Service SSO configuration
                    </CardTitle>
                    <CardDescription>
                        Set up Single Sign-On for{' '}
                        {organization?.display_name || organization?.name || 'this organization'}.
                        Generate secure configuration tickets that can be sent to organization
                        administrators to complete their SSO setup.
                    </CardDescription>
                </CardHeader>

                <CardContent className="p-6">
                    {/* Existing Ticket Section */}
                    {isLoadingExistingTicket ? (
                        <div className="flex items-center justify-center py-4">
                            <Spinner size="md" label={null} className="text-primary-500" />
                            <span className="ml-2 text-sm text-muted-foreground">
                                Checking for existing tickets…
                            </span>
                        </div>
                    ) : existingTicket && !ssoConnectionStatus?.exists ? (
                        <div className="rounded-lg border border-border bg-card p-4">
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <h3 className="mb-2 flex flex-wrap items-center gap-2 text-sm font-semibold text-neutral-950">
                                        SSO configuration ticket
                                        <span
                                            className={cn(
                                                ADMIN_BADGE_BASE_CLASS,
                                                isTicketExpired
                                                    ? ADMIN_BADGE_WARNING_CLASS
                                                    : ADMIN_BADGE_POSITIVE_CLASS,
                                                'gap-1',
                                            )}
                                        >
                                            {isTicketExpired ? (
                                                <Clock className="size-3 shrink-0" aria-hidden />
                                            ) : (
                                                <CircleCheck
                                                    className="size-3 shrink-0"
                                                    aria-hidden
                                                />
                                            )}
                                            {isTicketExpired ? 'Expired' : 'Active'}
                                        </span>
                                    </h3>
                                    <div className="space-y-2 text-sm text-neutral-600">
                                        <div className="flex items-center gap-2">
                                            <Clock className="w-4 h-4" aria-hidden />
                                            <span className="tabular-nums">
                                                Created: {formatDate(existingTicket.issuedAt)}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Clock className="w-4 h-4" aria-hidden />
                                            <span
                                                className={cn(
                                                    'tabular-nums',
                                                    isTicketExpired && 'text-amber-800',
                                                )}
                                            >
                                                Expires: {formatDate(existingTicket.expiresAt)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="mt-3 break-all rounded bg-muted p-2 font-mono text-xs text-neutral-950">
                                        {existingTicket.ticketUrl}
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-2 mt-4">
                                <Button onClick={handleCopyExistingUrl} variant="outline" size="sm">
                                    {existingTicketCopySuccess ? (
                                        <Check aria-hidden />
                                    ) : (
                                        <Copy aria-hidden />
                                    )}
                                    {existingTicketCopySuccess ? 'Copied!' : 'Copy URL'}
                                </Button>
                                <Button
                                    onClick={() => {
                                        setRevokeError(null);
                                        setIsRevokeConfirmOpen(true);
                                    }}
                                    variant="destructiveOutline"
                                    size="sm"
                                    disabled={isRevokingTicket}
                                    loading={isRevokingTicket}
                                >
                                    {!isRevokingTicket && (
                                        <Trash2 className="w-4 h-4" aria-hidden />
                                    )}
                                    {isRevokingTicket ? 'Revoking…' : 'Revoke'}
                                </Button>
                            </div>
                            {revokeError ? (
                                <InlineAlert tone="error" className="mt-3">
                                    {revokeError}
                                </InlineAlert>
                            ) : null}
                        </div>
                    ) : null}

                    {/* SSO Status Section */}
                    {connectionStatusError ? (
                        <div className="text-center py-12">
                            <InlineAlert tone="error" className="mb-6">
                                {connectionStatusError}
                            </InlineAlert>
                        </div>
                    ) : ssoConnectionStatus?.exists ? (
                        /* Existing SSO Connection */
                        <div className="text-center py-12">
                            <div className="w-20 h-20 mx-auto mb-6 bg-green-50 rounded-full flex items-center justify-center">
                                <ShieldCheck className="size-10 text-green-600" aria-hidden />
                            </div>
                            <h3 className="text-xl font-semibold text-foreground mb-4">
                                SSO connection active
                            </h3>
                            <div className="bg-green-50 border border-green-200 rounded-md p-6 mb-6 max-w-2xl mx-auto text-left">
                                <div className="space-y-3">
                                    <div className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
                                        <span className={fieldLabelClassName}>
                                            Connection name:
                                        </span>
                                        <span className="break-all font-mono text-sm text-neutral-950">
                                            {ssoConnectionStatus.connection?.id && auth0OrgUrl ? (
                                                <a
                                                    href={`${auth0OrgUrl}/connections/enterprise/waad/${ssoConnectionStatus.connection?.id}/settings`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={cn(
                                                        textLinkClassName,
                                                        'inline-flex cursor-pointer items-center gap-1 hover:underline',
                                                    )}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        window.open(
                                                            `${auth0OrgUrl}/connections/enterprise/waad/${ssoConnectionStatus.connection?.id}/settings`,
                                                            '_blank',
                                                        );
                                                        e.stopPropagation();
                                                    }}
                                                >
                                                    {ssoConnectionStatus.connectionName}
                                                    <ExternalLink
                                                        className="size-3.5 shrink-0"
                                                        aria-hidden
                                                    />
                                                    <span className="sr-only">
                                                        {' '}
                                                        (opens in a new tab)
                                                    </span>
                                                </a>
                                            ) : (
                                                ssoConnectionStatus.connectionName
                                            )}
                                        </span>
                                    </div>
                                    {ssoConnectionStatus.connection && (
                                        <>
                                            <div className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
                                                <span className={fieldLabelClassName}>
                                                    Strategy:
                                                </span>
                                                <span className="text-sm font-medium text-neutral-950">
                                                    {formatSsoStrategyLabel(
                                                        ssoConnectionStatus.connection.strategy,
                                                    )}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                            <p className="text-muted-foreground max-w-lg mx-auto">
                                Your organization already has an SSO connection configured. Users
                                can sign in using your identity provider.
                            </p>
                        </div>
                    ) : !existingTicket ? (
                        /* Setup SSO Section - only show when no connection AND no existing ticket */
                        <div className="text-center py-12">
                            <div className="w-20 h-20 mx-auto mb-6 bg-blue-50 rounded-full flex items-center justify-center">
                                <Shield className="size-10 text-primary-500" aria-hidden />
                            </div>
                            <h3 className="text-xl font-semibold text-foreground mb-6">
                                Configure organization SSO
                            </h3>

                            <Button
                                onClick={handleSetupSSOClick}
                                className="mx-auto mb-6 flex items-center gap-2"
                                size="lg"
                            >
                                <Ticket aria-hidden />
                                Set up SSO ticket
                            </Button>

                            <p className="text-muted-foreground max-w-lg mx-auto">
                                Generate secure SSO configuration tickets using Auth0 Self-Service
                                Profiles. These tickets can be shared with organization
                                administrators to set up their identity provider integrations.
                            </p>
                        </div>
                    ) : null}
                </CardContent>
            </Card>

            {/* How It Works Card */}
            <Card>
                <CardHeader className="bg-muted/40">
                    <CardTitle as="h2" className={overlineClassName}>
                        How it works
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                    <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">
                            <span className="font-medium text-foreground">Step 1:</span> Select the
                            appropriate Self-Service Profile that matches your organization's SSO
                            requirements.
                        </p>
                        <p className="text-sm text-muted-foreground">
                            <span className="font-medium text-foreground">Step 2:</span> Configure
                            domain aliases for your SSO connection (optional).
                        </p>
                        <p className="text-sm text-muted-foreground">
                            <span className="font-medium text-foreground">Step 3:</span> Generate a
                            secure configuration ticket that expires in 5 days and can only be used
                            once.
                        </p>
                        <p className="text-sm text-muted-foreground">
                            <span className="font-medium text-foreground">Step 4:</span> Copy the
                            generated URL and send it to your organization administrator to complete
                            the SSO setup.
                        </p>
                    </div>
                </CardContent>
            </Card>

            <ConfirmDialog
                open={isRevokeConfirmOpen}
                title="Revoke SSO ticket?"
                description="The ticket URL will stop working. To finish SSO setup, the organization admin will need a new ticket."
                confirmLabel="Revoke ticket"
                pendingLabel="Revoking…"
                destructive
                onCancel={() => setIsRevokeConfirmOpen(false)}
                onConfirm={async () => {
                    await handleRevokeTicket();
                    setIsRevokeConfirmOpen(false);
                }}
            />

            <ConfirmDialog
                open={showConfirmationModal}
                title="Replace existing ticket?"
                description="An active SSO configuration ticket already exists for this organization. Generating a new ticket will automatically deactivate the existing one. Are you sure you want to continue?"
                confirmLabel="Replace ticket"
                destructive
                onCancel={() => setShowConfirmationModal(false)}
                onConfirm={handleConfirmNewTicket}
            />

            <DialogFrame
                open={isModalOpen}
                title="SSO configuration wizard"
                onClose={ticketUrl ? () => void handleSuccessfulCompletion() : handleCloseModal}
                headerAccessory={
                    <ol aria-label="Wizard progress" className="flex items-center gap-2">
                        <li
                            aria-current={currentStep === 1 ? 'step' : undefined}
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${currentStep >= 1 ? 'bg-primary-500 text-white' : 'bg-muted text-muted-foreground'}`}
                        >
                            {currentStep > 1 ? (
                                <Check className="size-4" aria-hidden />
                            ) : (
                                <span aria-hidden>1</span>
                            )}
                            <span className="sr-only">
                                Step 1 of 3{currentStep > 1 ? ', completed' : ''}
                            </span>
                        </li>
                        <li aria-hidden="true" className="w-8 h-0.5 bg-muted" />
                        <li
                            aria-current={currentStep === 2 ? 'step' : undefined}
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${currentStep >= 2 ? 'bg-primary-500 text-white' : 'bg-muted text-muted-foreground'}`}
                        >
                            {currentStep > 2 ? (
                                <Check className="size-4" aria-hidden />
                            ) : (
                                <span aria-hidden>2</span>
                            )}
                            <span className="sr-only">
                                Step 2 of 3{currentStep > 2 ? ', completed' : ''}
                            </span>
                        </li>
                        <li aria-hidden="true" className="w-8 h-0.5 bg-muted" />
                        <li
                            aria-current={currentStep === 3 ? 'step' : undefined}
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${currentStep >= 3 ? 'bg-primary-500 text-white' : 'bg-muted text-muted-foreground'}`}
                        >
                            <span aria-hidden>3</span>
                            <span className="sr-only">Step 3 of 3</span>
                        </li>
                    </ol>
                }
                panelClassName="max-w-2xl"
            >
                {/* Step 1: Profile Selection */}
                {currentStep === 1 && (
                    <div className="space-y-6">
                        <div className="text-center">
                            <div className="w-16 h-16 mx-auto mb-4 bg-muted/40 rounded-lg flex items-center justify-center">
                                <ClipboardList
                                    className="size-8 text-muted-foreground"
                                    aria-hidden
                                />
                            </div>
                            <h3 className="text-lg font-semibold text-foreground mb-2">
                                Select Self-Service Profile
                            </h3>
                            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                                Choose the Self-Service Profile configuration that will be used to
                                generate the SSO setup ticket.
                            </p>

                            {profilesError && (
                                <InlineAlert tone="error" className="mt-4">
                                    {profilesError}
                                </InlineAlert>
                            )}

                            {isLoadingProfiles && (
                                <div className="flex items-center justify-center gap-2">
                                    <Spinner size="xs" label={null} className="text-current" />
                                    Loading profiles…
                                </div>
                            )}
                        </div>

                        {profiles.length > 0 && (
                            <div className="space-y-3">
                                <h4 className="text-sm font-semibold text-foreground">
                                    Available profiles
                                </h4>
                                {profiles.map((profile) => (
                                    <div
                                        key={profile.id}
                                        className="cursor-pointer rounded-lg border p-4 transition-colors hover:border-neutral-300 hover:bg-neutral-50 active:bg-neutral-100"
                                        onClick={() => handleProfileSelect(profile)}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex-1">
                                                <h5 className="text-sm font-medium text-foreground">
                                                    {profile.name}
                                                </h5>
                                                {profile.description && (
                                                    <p className="text-xs text-muted-foreground mt-1">
                                                        {profile.description}
                                                    </p>
                                                )}
                                            </div>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                aria-label={`Select ${profile.name}`}
                                            >
                                                Select
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Step 2: Domain Aliases Configuration */}
                {currentStep === 2 && selectedProfile && (
                    <div className="space-y-6">
                        {/* Selected Profile Info */}
                        <div className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                            <div>
                                <h4 className="text-sm font-medium">
                                    Selected profile: {selectedProfile.name}
                                </h4>
                                {selectedProfile.description && (
                                    <p className="text-xs text-muted-foreground">
                                        {selectedProfile.description}
                                    </p>
                                )}
                            </div>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={handleBackToProfileSelection}
                            >
                                Change
                            </Button>
                        </div>

                        {/* Domain Aliases Configuration */}
                        <div className="text-center">
                            <div className="w-16 h-16 mx-auto mb-4 bg-muted/40 rounded-lg flex items-center justify-center">
                                <Globe className="size-8 text-muted-foreground" aria-hidden />
                            </div>
                            <h3 className="text-lg font-semibold text-foreground mb-6">
                                Configure domain aliases
                            </h3>
                        </div>

                        <form
                            className="space-y-4"
                            noValidate
                            onSubmit={(event) => {
                                event.preventDefault();
                                handleContinueToGenerate();
                            }}
                        >
                            <Field
                                label="Domain aliases (optional)"
                                hint="Comma-separated list of verified domains that can be authenticated in the identity provider. Use domain names only (no @ symbols)."
                                error={domainAliasError ?? undefined}
                            >
                                <Input
                                    ref={domainAliasInputRef}
                                    id="domainAliases"
                                    value={domainAliases}
                                    onChange={(e) => handleDomainAliasChange(e.target.value)}
                                    onBlur={handleDomainAliasBlur}
                                    placeholder="e.g., company.com, app.company.com (no @ symbols)"
                                    inputMode="url"
                                    autoComplete="off"
                                    autoCapitalize="none"
                                    autoCorrect="off"
                                    spellCheck={false}
                                />
                            </Field>

                            <p className="text-muted-foreground max-w-md mx-auto text-center">
                                Domain aliases allow users from these verified domains to
                                authenticate using this SSO connection. Leave blank if not required.
                            </p>

                            {/* Navigation Buttons */}
                            <DialogFooter className="justify-between">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={handleBackToProfileSelection}
                                >
                                    Back
                                </Button>
                                <Button type="submit">Continue</Button>
                            </DialogFooter>
                        </form>
                    </div>
                )}

                {/* Step 3: Generate Ticket */}
                {currentStep === 3 && selectedProfile && (
                    <div className="space-y-6">
                        {/* Selected Profile Info */}
                        <div className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                            <div>
                                <h4 className="text-sm font-medium">
                                    Selected profile: {selectedProfile.name}
                                </h4>
                                {selectedProfile.description && (
                                    <p className="text-xs text-muted-foreground">
                                        {selectedProfile.description}
                                    </p>
                                )}
                            </div>
                            <Button size="sm" variant="outline" onClick={handleBackToDomainAliases}>
                                Change
                            </Button>
                        </div>

                        {/* Domain Aliases Summary */}
                        {domainAliases && (
                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-sm text-blue-800">
                                    <span className="font-medium">Domain aliases:</span>{' '}
                                    {domainAliases}
                                </p>
                            </div>
                        )}

                        {/* Generate Ticket Section - Hide when ticket is generated */}
                        {!ticketUrl && (
                            <div className="text-center">
                                <div className="w-16 h-16 mx-auto mb-4 bg-muted/40 rounded-lg flex items-center justify-center">
                                    <KeyRound
                                        className="size-8 text-muted-foreground"
                                        aria-hidden
                                    />
                                </div>
                                <h3 className="text-lg font-semibold text-foreground mb-6">
                                    Generate SSO configuration ticket
                                </h3>

                                <Button
                                    onClick={handleGenerateTicket}
                                    disabled={isGeneratingTicket}
                                    loading={isGeneratingTicket}
                                    className="mx-auto mb-6 flex items-center gap-2"
                                >
                                    {!isGeneratingTicket && <Ticket aria-hidden />}
                                    {isGeneratingTicket
                                        ? 'Generating ticket…'
                                        : 'Generate SSO configuration ticket'}
                                </Button>

                                <p className="text-muted-foreground max-w-md mx-auto">
                                    Create a secure configuration link using the selected profile
                                    that can be sent to the organization administrator to set up
                                    their SSO integration.
                                </p>

                                {ticketError && (
                                    <InlineAlert
                                        tone="error"
                                        title="Couldn't generate SSO configuration ticket"
                                        className="mt-4"
                                    >
                                        {ticketError.split('\n').map((line, index) => (
                                            <div key={index}>
                                                {line.startsWith('• ') ? (
                                                    <div className="ml-2 flex">
                                                        <span className="mr-2">•</span>
                                                        <span>{line.substring(2)}</span>
                                                    </div>
                                                ) : line.trim() === 'Suggested actions:' ? (
                                                    <div className="mt-3 font-medium">{line}</div>
                                                ) : line.trim() ? (
                                                    <div className={index > 0 ? 'mt-1' : ''}>
                                                        {line}
                                                    </div>
                                                ) : null}
                                            </div>
                                        ))}
                                    </InlineAlert>
                                )}
                            </div>
                        )}

                        {/* Generated Ticket Display */}
                        {ticketUrl && (
                            <div className="border-t pt-6">
                                <h4 className="text-sm font-semibold text-foreground mb-3">
                                    Generated SSO configuration link
                                </h4>
                                <div className="space-y-3">
                                    <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs text-green-700 font-mono break-all">
                                                {ticketUrl}
                                            </p>
                                        </div>
                                        <Button
                                            ref={copyButtonRef}
                                            size="sm"
                                            variant="outline"
                                            onClick={handleCopyUrl}
                                            className="flex-shrink-0 w-20 justify-center"
                                        >
                                            {copySuccess ? (
                                                <>
                                                    <Check className="text-green-600" aria-hidden />
                                                    Copied!
                                                </>
                                            ) : (
                                                <>
                                                    <Copy aria-hidden />
                                                    Copy
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                        <p>
                                            <strong>Instructions:</strong> Copy this link and send
                                            it to the organization administrator. The link expires
                                            in 5 days and can only be used once.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Navigation Buttons */}
                        <DialogFooter className="justify-between">
                            {!ticketUrl && (
                                <Button variant="outline" onClick={handleBackToDomainAliases}>
                                    Back
                                </Button>
                            )}
                            {ticketUrl && (
                                <Button className="ml-auto" onClick={handleSuccessfulCompletion}>
                                    Done
                                </Button>
                            )}
                        </DialogFooter>
                    </div>
                )}
            </DialogFrame>
        </div>
    );
}
